'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties
} from 'react';
import Image from 'next/image';
import { AnimatePresence, motion, usePresence } from 'framer-motion';
import type { Variants } from 'framer-motion';
import type { Data } from '@strapi/strapi';
import { CheckmarkCircleFill } from '@baseline/ui';
import commonStyles from '@/app/(website)/commonStyles.module.scss';
import styles from './featureListScroll.module.scss';
import { buildImageSrc } from '@/services/formatUtils';
import { useParallaxScroll } from '@/hooks/useParallaxScroll';
import { useFeatureListScrollState } from '@/hooks/useFeatureListScrollState';

const FALLBACK_CARD_DIMENSIONS = {
  width: 960,
  height: 720
};

type Padding = {
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
};

type FeatureEntry = Partial<Data.Component<'shared.product-feature-list'>>;
type FeatureDetail = NonNullable<FeatureEntry['detail_list']>[number];

export type FeatureListScrollProps = {
  features?: FeatureEntry[] | null;
  padding?: Padding;
  disableParallaxOnMobile?: boolean;
};

const buildPaddingStyles = (padding?: Padding) =>
  padding
    ? {
        paddingTop: padding.top,
        paddingBottom: padding.bottom,
        paddingLeft: padding.left,
        paddingRight: padding.right
      }
    : undefined;

const PREVIEW_VARIANTS: Variants = {
  enter: {
    opacity: 0
  },
  center: {
    opacity: 1
  },
  exit: {
    opacity: 0
  }
};

const SLIDE_TRANSITION = {
  duration: 0.85,
  ease: [0.18, 1, 0.3, 1] as const
};

const CONTENT_VARIANTS: Variants = {
  enter: (direction: number = 1) => ({
    opacity: 0,
    y: direction > 0 ? 64 : -64
  }),
  center: {
    opacity: 1,
    y: 0
  },
  exit: (direction: number = 1) => ({
    opacity: 0,
    y: direction > 0 ? -64 : 64
  })
};

const BODY_VARIANTS: Variants = {
  enter: {
    opacity: 0
  },
  center: {
    opacity: 1
  },
  exit: {
    opacity: 0
  }
};

const BODY_TRANSITION = {
  duration: 0.65,
  ease: [0.18, 1, 0.3, 1] as const
};

const HEIGHT_TRANSITION = {
  duration: 0.35,
  ease: [0.33, 1, 0.68, 1] as const
};

const PREVIEW_TRANSITION = {
  duration: 0.45,
  ease: [0.27, 0.99, 0.5, 1] as const
};

const mapFeatureImage = (entry?: FeatureEntry | null) => ({
  url: entry?.feature_image?.url,
  alternativeText: entry?.feature_image?.alternativeText,
  width: entry?.feature_image?.width ?? FALLBACK_CARD_DIMENSIONS.width,
  height: entry?.feature_image?.height ?? FALLBACK_CARD_DIMENSIONS.height
});

type FeatureCardProps = {
  feature: FeatureEntry;
  index: number;
  isActive: boolean;
  registerCard: (index: number, node: HTMLDivElement | null) => void;
};

function FeatureCard({
  feature,
  index,
  isActive,
  registerCard
}: FeatureCardProps) {
  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      registerCard(index, node);
    },
    [index, registerCard]
  );

  const image = mapFeatureImage(feature);

  return (
    <div
      ref={setRef}
      className={`${styles.cardWrap} ${isActive ? styles.cardActive : styles.cardInactive}`}
    >
      <Image
        className={styles.cardImage}
        src={buildImageSrc(image.url, '/feature-image.png')}
        alt={image.alternativeText || `Feature image ${index + 1}`}
        width={image.width ?? FALLBACK_CARD_DIMENSIONS.width}
        height={image.height ?? FALLBACK_CARD_DIMENSIONS.height}
        sizes="(max-width: 1024px) 100vw, 36rem"
        priority={index === 0}
      />
    </div>
  );
}

type AnimatedFeatureContentProps = {
  feature: FeatureEntry;
  featureIndex: number;
  animationDirection: number;
  shouldAnimate: boolean;
  setNode: (node: HTMLDivElement | null) => void;
};

function AnimatedFeatureContent({
  feature,
  featureIndex,
  animationDirection,
  shouldAnimate,
  setNode
}: AnimatedFeatureContentProps) {
  const [isPresent] = usePresence();
  const details = (feature?.detail_list ?? []) as FeatureDetail[];
  const nodeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isPresent) {
      return;
    }

    const node = nodeRef.current;

    if (!node) {
      return;
    }

    setNode(node);

    return () => {
      setNode(null);
    };
  }, [isPresent, setNode]);

  return (
    <motion.div
      ref={nodeRef}
      className={styles.featureContentLayer}
      custom={animationDirection}
      variants={CONTENT_VARIANTS}
      initial={shouldAnimate ? 'enter' : false}
      animate="center"
      exit="exit"
      transition={SLIDE_TRANSITION}
      style={{
        position: isPresent ? 'relative' : 'absolute',
        inset: isPresent ? undefined : 0,
        width: '100%',
        pointerEvents: isPresent ? 'auto' : 'none'
      }}
    >
      <h2
        className={`${commonStyles.sectionSubheadingH2} ${styles.featureTitle}`}
      >
        {feature?.header || `Feature ${featureIndex + 1}`}
      </h2>
      <motion.div
        className={styles.featureBody}
        custom={animationDirection}
        variants={BODY_VARIANTS}
        initial={shouldAnimate ? 'enter' : false}
        animate="center"
        exit="exit"
        transition={BODY_TRANSITION}
        aria-hidden={false}
      >
        {feature?.description ? (
          <p className={`${commonStyles.sectionBody} ${styles.summary}`}>
            {feature.description}
          </p>
        ) : null}
        {details.length ? (
          <ul className={styles.list}>
            {details.map((item) => (
              <li key={item.id ?? item.detail} className={styles.listItem}>
                <CheckmarkCircleFill className={styles.listIcon} aria-hidden />
                <span
                  className={`${commonStyles.sectionBody} ${styles.listCopy}`}
                >
                  {item.detail}
                </span>
              </li>
            ))}
          </ul>
        ) : null}
      </motion.div>
    </motion.div>
  );
}

type AnimatedPreviewTitleProps = {
  title?: string | null;
  featureIndex: number;
  shouldAnimate: boolean;
};

function AnimatedPreviewTitle({
  title,
  featureIndex,
  shouldAnimate
}: AnimatedPreviewTitleProps) {
  const [isPresent] = usePresence();

  return (
    <motion.h2
      className={`${commonStyles.sectionSubheadingH2} ${styles.featureTitle}`}
      variants={PREVIEW_VARIANTS}
      initial={shouldAnimate ? 'enter' : false}
      animate="center"
      exit="exit"
      transition={PREVIEW_TRANSITION}
      aria-hidden
      style={{
        position: isPresent ? 'relative' : 'absolute',
        inset: isPresent ? undefined : 0,
        width: '100%'
      }}
    >
      {title || `Feature ${featureIndex + 1}`}
    </motion.h2>
  );
}

export default function FeatureListScroll({
  features = [],
  padding,
  disableParallaxOnMobile = true
}: FeatureListScrollProps) {
  const featureGroups = useMemo(
    () => (features ?? []).filter(Boolean) as FeatureEntry[],
    [features]
  );

  const paddingStyles = useMemo(() => buildPaddingStyles(padding), [padding]);
  const { sectionRef, parallaxValue, isParallaxEnabled } = useParallaxScroll({
    disableOnMobile: disableParallaxOnMobile,
    outputRange: [0, -160]
  });
  const {
    leftColumnRef,
    registerCard,
    activeIndex,
    previousIndex,
    anchorOffset,
    scrollBuffer
  } = useFeatureListScrollState({
    itemCount: featureGroups.length
  });
  const [featureContentHeight, setFeatureContentHeight] = useState<
    number | null
  >(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const sectionStyle = useMemo(() => {
    const nextStyles: CSSProperties = {
      ...(paddingStyles ?? {})
    };

    (nextStyles as Record<string, string | number>)[
      '--feature-scroll-anchor-offset'
    ] = `${anchorOffset}px`;
    (nextStyles as Record<string, string | number>)[
      '--feature-scroll-buffer-dynamic'
    ] = `${scrollBuffer}px`;

    return nextStyles;
  }, [paddingStyles, anchorOffset, scrollBuffer]);

  const setActiveContentNode = useCallback((node: HTMLDivElement | null) => {
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;

    if (!node) {
      return;
    }

    const measure = () => {
      const nextHeight = node.getBoundingClientRect().height;
      if (nextHeight > 0) {
        setFeatureContentHeight(nextHeight);
      }
    };

    measure();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => {
        measure();
      });
      observer.observe(node);
      resizeObserverRef.current = observer;
    }
  }, []);

  useEffect(() => {
    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, []);

  const activeFeature = featureGroups[activeIndex];
  const previewFeature = featureGroups[activeIndex + 1];
  const scrollDirection =
    activeIndex === previousIndex ? 0 : activeIndex > previousIndex ? 1 : -1;
  const hasScrollMovement = scrollDirection !== 0;
  const animationDirection = hasScrollMovement ? scrollDirection : 1;
  const shouldAnimate = hasScrollMovement;

  if (!featureGroups.length) {
    return null;
  }

  return (
    <section
      ref={sectionRef}
      className={`${commonStyles.sectionContent} ${styles.featureListScroll}`}
      style={sectionStyle}
    >
      <div className={styles.desktopLayout}>
        <div className={styles.leftColumn} ref={leftColumnRef}>
          <ul className={styles.featureStack}>
            <li className={`${styles.featureItem} ${styles.featureItemActive}`}>
              <motion.div
                className={styles.featureContent}
                style={
                  featureContentHeight != null
                    ? {
                        height: featureContentHeight,
                        minHeight: featureContentHeight
                      }
                    : undefined
                }
                animate={
                  featureContentHeight != null
                    ? { height: featureContentHeight }
                    : undefined
                }
                transition={HEIGHT_TRANSITION}
                initial={false}
              >
                <AnimatePresence
                  initial={false}
                  custom={animationDirection}
                  mode="sync"
                >
                  {activeFeature ? (
                    <AnimatedFeatureContent
                      key={
                        activeFeature.id ?? activeFeature.header ?? activeIndex
                      }
                      feature={activeFeature}
                      featureIndex={activeIndex}
                      animationDirection={animationDirection}
                      shouldAnimate={shouldAnimate}
                      setNode={setActiveContentNode}
                    />
                  ) : null}
                </AnimatePresence>
              </motion.div>
            </li>
            <li className={`${styles.featureItem} ${styles.featureItemNext}`}>
              <div className={styles.previewHeadingSlot}>
                <AnimatePresence initial={false} mode="sync">
                  {previewFeature ? (
                    <AnimatedPreviewTitle
                      key={
                        previewFeature.id ??
                        previewFeature.header ??
                        `${activeIndex + 1}-preview`
                      }
                      title={previewFeature?.header}
                      featureIndex={activeIndex + 1}
                      shouldAnimate={hasScrollMovement}
                    />
                  ) : null}
                </AnimatePresence>
              </div>
            </li>
          </ul>
        </div>
        <div className={styles.rightColumn}>
          <motion.div
            className={styles.cardsRail}
            style={{ y: isParallaxEnabled ? parallaxValue : 0 }}
          >
            {featureGroups.map((feature, index) => (
              <FeatureCard
                key={feature?.id ?? feature?.header ?? index}
                feature={feature}
                index={index}
                isActive={index === activeIndex}
                registerCard={registerCard}
              />
            ))}
          </motion.div>
        </div>
      </div>
      <div className={styles.mobileStack}>
        {featureGroups.map((feature, index) => {
          const image = mapFeatureImage(feature);
          const details = feature?.detail_list ?? [];

          return (
            <div
              key={feature?.id ?? feature?.header ?? index}
              className={styles.mobileItem}
            >
              <div className={styles.mobileContent}>
                <h2
                  className={`${commonStyles.sectionSubheadingH2} ${styles.featureTitle}`}
                >
                  {feature?.header || `Feature ${index + 1}`}
                </h2>
                {feature?.description ? (
                  <p
                    className={`${commonStyles.sectionBody} ${styles.summary}`}
                  >
                    {feature.description}
                  </p>
                ) : null}
                {details.length ? (
                  <ul className={styles.list}>
                    {details.map((item) => (
                      <li
                        key={item.id ?? item.detail}
                        className={styles.listItem}
                      >
                        <CheckmarkCircleFill
                          className={styles.listIcon}
                          aria-hidden
                        />
                        <span
                          className={`${commonStyles.sectionBody} ${styles.listCopy}`}
                        >
                          {item.detail}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
              <div className={styles.mobileImageWrap}>
                <Image
                  className={styles.cardImage}
                  src={buildImageSrc(image.url, '/feature-image.png')}
                  alt={image.alternativeText || `Feature image ${index + 1}`}
                  width={image.width ?? FALLBACK_CARD_DIMENSIONS.width}
                  height={image.height ?? FALLBACK_CARD_DIMENSIONS.height}
                  sizes="100vw"
                  priority={index === 0}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
